import { Fragment, useCallback, useEffect, useState } from 'react'
import Layout from '../../components/Layout'
import { getCasinoOpenBets, voidCasinoBets } from '../../services/api'
import SettlementVoidModal from '../../components/SettlementVoidModal'
import CasinoSettledVoid from './casino-settled-void'

/**
 * Casino Settlement — wo casino bets jo settle HI NAHI hue (status
 * open/processing), aur unka void.
 *
 * "Void Bets" page se alag hai: wahan bet pehle settle ho chuki hoti hai aur
 * settlement ke BAAD void karni hoti hai. Yahan bet kabhi settle hi nahi hui —
 * uska placement lock user ke cash aur exposure me atka rehta hai jab tak koi
 * void na kare.
 *
 * Void hamesha (user + round) ke level par hota hai: server aadha round void
 * karne se mana kar deta hai, kyunki book market ka exposure row poore market
 * ka min() hota hai. Isliye "Void Bet" button sirf tab dikhta hai jab us round
 * par us user ki wahi ek bet ho.
 */
const fmt = (v) => Number(v || 0).toFixed(2)

const ago = (ts) => {
  if (!ts) return '-'
  const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000)
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  return `${Math.floor(hrs / 24)}d`
}

export default function CasinoSettlement() {
  // Do case hain aur dono ka paisa alag chalta hai:
  //   unsettled → sirf cash me refund, inr_balance waisa hi
  //   settled   → poori settlement ulti: cash + inr dono, P/L, points, sab
  const [tab, setTab] = useState('unsettled')
  const [rounds, setRounds] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [expanded, setExpanded] = useState(null)
  const [confirm, setConfirm] = useState(null)
  const [txnPassword, setTxnPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)

  const fetchRounds = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const resp = await getCasinoOpenBets()
      setRounds(resp?.success && Array.isArray(resp.data) ? resp.data : [])
      if (resp && !resp.success) setError(resp.error || 'Failed to load')
    } catch (err) {
      setRounds([])
      setError(err?.response?.data?.error || err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchRounds() }, [fetchRounds])

  const openConfirm = (round, bet = null) => {
    setTxnPassword('')
    setResult(null)
    setConfirm({ round, bet })
  }

  const submitVoid = async () => {
    if (!confirm) return
    setSubmitting(true)
    setResult(null)
    try {
      const betIds = confirm.bet ? [confirm.bet.betId] : confirm.round.bets.map((b) => b.betId)
      const resp = await voidCasinoBets({ bet_ids: betIds, transactionPassword: txnPassword })
      if (resp?.success) {
        setResult({ ok: true, message: resp.message || 'Voided' })
        setConfirm(null)
        fetchRounds()
      } else {
        setResult({ ok: false, message: resp?.error || resp?.message || 'Failed' })
      }
    } catch (err) {
      setResult({
        ok: false,
        message: err?.response?.data?.error || err?.response?.data?.message || err.message
      })
    } finally {
      setSubmitting(false)
    }
  }

  const totalBets = rounds.reduce((s, r) => s + r.betCount, 0)
  const totalLocked = rounds.reduce((s, r) => s + Number(r.totalExposer || 0), 0)

  return (
    <Layout title="Casino Settlement - Diamond Admin">
      <div className="listing-grid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-flex align-items-center justify-content-between">
              <h4 className="mb-0 font-size-18">Casino Settlement (Unsettled Bets)</h4>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${tab === 'unsettled' ? 'active' : ''}`}
                  onClick={() => setTab('unsettled')}
                >
                  Unsettled (refund to cash)
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${tab === 'settled' ? 'active' : ''}`}
                  onClick={() => setTab('settled')}
                >
                  Settled (reverse cash + inr)
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {tab === 'settled' ? <CasinoSettledVoid /> : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <strong>{rounds.length}</strong> stuck round{rounds.length !== 1 ? 's' : ''} ·{' '}
                        <strong>{totalBets}</strong> bet{totalBets !== 1 ? 's' : ''} · locked exposure{' '}
                        <span className="text-danger fw-bold">{fmt(totalLocked)}</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={fetchRounds}
                        disabled={loading}
                      >
                        {loading ? 'Loading...' : 'Refresh'}
                      </button>
                    </div>

                    {error && <div className="alert alert-danger py-2">{error}</div>}
                    {result && !confirm && (
                      <div className={`alert py-2 ${result.ok ? 'alert-success' : 'alert-danger'}`}>
                        {result.message}
                      </div>
                    )}

                    <div className="table-responsive">
                      <table className="table table-bordered table-sm mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>User</th>
                            <th>Game</th>
                            <th>Round</th>
                            <th className="text-end">Bets</th>
                            <th className="text-end">Stake</th>
                            <th className="text-end">Locked</th>
                            <th className="text-center">Age</th>
                            <th className="text-center" style={{ minWidth: '210px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr><td colSpan={8} className="text-center py-4">Loading...</td></tr>
                          ) : rounds.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="text-center py-4">
                                No unsettled casino bets 🎉
                              </td>
                            </tr>
                          ) : (
                            rounds.map((r) => (
                              <Fragment key={r.groupKey}>
                                <tr>
                                  <td>
                                    <div className="fw-bold">{r.username}</div>
                                    <small className="text-muted">#{r.userId}</small>
                                  </td>
                                  <td>{r.gameName || '-'}</td>
                                  <td><small className="text-muted">{r.eventId}</small></td>
                                  <td className="text-end">{r.betCount}</td>
                                  <td className="text-end">{fmt(r.totalStake)}</td>
                                  <td className="text-end text-danger fw-bold">{fmt(r.totalExposer)}</td>
                                  <td className="text-center">{ago(r.oldestAt)}</td>
                                  <td className="text-center">
                                    <div className="btn-group btn-group-sm">
                                      <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setExpanded(expanded === r.groupKey ? null : r.groupKey)}
                                      >
                                        {expanded === r.groupKey ? 'Hide Bets' : 'View Bets'}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => openConfirm(r)}
                                      >
                                        Void Round
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                                {expanded === r.groupKey && (
                                  <tr>
                                    <td colSpan={8} className="bg-light p-3">
                                      <div className="table-responsive">
                                        <table className="table table-sm table-bordered mb-0">
                                          <thead className="table-light">
                                            <tr>
                                              <th>Bet ID</th>
                                              <th>Selection</th>
                                              <th className="text-center">Type</th>
                                              <th className="text-end">Odds</th>
                                              <th className="text-end">Stake</th>
                                              <th className="text-end">Exposer</th>
                                              <th className="text-center">Status</th>
                                              <th className="text-center">Action</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {r.bets.map((b) => (
                                              <tr key={b.betId}>
                                                <td>#{b.betId}</td>
                                                <td>{b.selection}</td>
                                                <td className="text-center">
                                                  <span className={`badge px-2 ${b.betType === 'lay' ? 'bg-danger' : 'bg-primary'}`}>
                                                    {String(b.betType || '-').toUpperCase()}
                                                  </span>
                                                </td>
                                                <td className="text-end">{b.odds}</td>
                                                <td className="text-end">{fmt(b.stake)}</td>
                                                <td className="text-end">{fmt(b.exposer)}</td>
                                                <td className="text-center">
                                                  <span className="badge bg-secondary px-2">
                                                    {String(b.status || '').toUpperCase()}
                                                  </span>
                                                </td>
                                                <td className="text-center">
                                                  {r.betCount === 1 ? (
                                                    <button
                                                      type="button"
                                                      className="btn btn-primary btn-sm"
                                                      onClick={() => openConfirm(r, b)}
                                                    >
                                                      Void Bet
                                                    </button>
                                                  ) : (
                                                    <button
                                                      type="button"
                                                      className="btn btn-outline-secondary btn-sm"
                                                      disabled
                                                      title="Is round me ek se zyada bets hain — upar se Void Round use karo"
                                                    >
                                                      Use Void Round
                                                    </button>
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <SettlementVoidModal
        show={!!confirm}
        title={confirm?.bet ? 'Void Casino Bet' : 'Void Casino Round'}
        onHide={() => setConfirm(null)}
        onConfirm={submitVoid}
        submitting={submitting}
        requirePassword
        password={txnPassword}
        onPasswordChange={setTxnPassword}
        error={result && !result.ok ? result.message : ''}
      >
        {confirm && (
          <>
            <p className="mb-2">
              {confirm.bet ? (
                <>Void bet <strong>#{confirm.bet.betId}</strong> ({confirm.bet.selection})</>
              ) : (
                <>
                  Void <strong>all {confirm.round.betCount} bet(s)</strong> of round{' '}
                  <strong>{confirm.round.eventId}</strong>
                </>
              )}{' '}
              for user <strong>{confirm.round.username}</strong> on{' '}
              <strong>{confirm.round.gameName}</strong>?
            </p>
            <p className="mb-0 text-muted">
              Refund to cash:{' '}
              <span className="text-success fw-bold">
                {fmt(-(confirm.bet ? confirm.bet.exposer : confirm.round.totalExposer))}
              </span>
              . Main balance (inr) unchanged — no win/loss is booked. This cannot be undone.
            </p>
          </>
        )}
      </SettlementVoidModal>
    </Layout>
  )
}
